/* eslint-disable @typescript-eslint/consistent-type-definitions */
import { Signal, computed, inject } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SignalStoreFeature, signalStoreFeature, withState, withComputed, withMethods, EmptyFeatureResult } from '@ngrx/signals';

import { AppConstants } from '../../config/constants.config';

export function getModalStateKeys(config?: { collection?: string }) {
	const prop = config?.collection;
	return {
		modalKey: prop ? `${config.collection}ModalRef` : 'modalRef',
	};
}

export interface ModalSlice {
	modalRef: NgbModalRef;
}

export interface ModalSignals {
	hasOpenModals: Signal<boolean>;
}

export type ModalMethods = {
	openModal: (content: any, options?: NgbModalOptions) => NgbModalRef,
	dismissAll: () => void;
}

export type NamedModalSlice<Collection extends string> = {
	[K in Collection as `${K}ModalRef`]: Signal<NgbModalRef>;
}

export function withModalService<Collection extends string>(config: {
	collection: Collection;
}): SignalStoreFeature<
	EmptyFeatureResult,
	{
		state: NamedModalSlice<Collection>;
		props: ModalSignals;
		methods: ModalMethods;
	}
>;
export function withModalService<_>(): SignalStoreFeature<
	EmptyFeatureResult,
	{
		state: ModalSlice;
		props: ModalSignals;
		methods: ModalMethods;
	}
>;
export function withModalService<Collection extends string>(config?: {
	collection: Collection;
}) {
	const { modalKey } = getModalStateKeys(config);

	return signalStoreFeature(
		{},

		withState({ [modalKey]: null }),
		withMethods(({ ...store }) => {
			const modalService = inject(NgbModal);
			return store['openModal'] ? {} : {
				openModal: (component: any, options: NgbModalOptions = AppConstants.DEFAULT_MODAL_OPTIONS) => {
					const modalRef = modalService.open(component, options);
					// FIXME: Disable until we find why it fails in tests
					// patchState(store, s => ({ ...s, ...setModalRef(modalRef, config?.collection) }))
					return modalRef;
				},
				dismissAll: _ => modalService.dismissAll(),
			}
		}),
		withComputed((store: Record<string, Signal<unknown>>) => {
			const modalService = inject(NgbModal);

			return store['hasOpenModals'] ? {} : {
				hasOpenModals: computed(() => modalService.hasOpenModals()),
			};
		}),
	);
}

export function setModalRef<Prop extends string>(
	modalRef: NgbModalRef,
	prop?: Prop,
): NamedModalSlice<Prop> | ModalSlice {
	if (prop) {
		return { [`${prop}ModalRef`]: modalRef } as unknown as NamedModalSlice<Prop>;
	}

	return { modalRef };
}
